// src/components/personal/sections/ClientsSection/utils/clientFilters.ts
import { Client, ClientFilters } from '../types/types';

/**
 * Создает объект с пустыми фильтрами по умолчанию
 */
export const getEmptyFilters = (): ClientFilters => ({
  ordersFrom: '',
  ordersTo: '',
  amountFrom: '',
  amountTo: '',
  status: 'all',
  lastOrderFrom: '',
  lastOrderTo: '',
  leadsource: ''
});

/**
 * Улучшенная функция фильтрации списка клиентов
 * с исправленным поиском по ID и телефону
 * 
 * @param clients Исходный список клиентов
 * @param filters Объект с параметрами фильтрации
 * @param searchQuery Строка поиска (ID, телефон)
 * @returns Отфильтрованный список клиентов
 */
export const filterClients = (
  clients: Client[],
  filters: ClientFilters,
  searchQuery: string
): Client[] => {
  // Для отладки
  console.log(`Фильтрация ${clients.length} клиентов:`, { 
    filters: JSON.stringify(filters), 
    searchQuery 
  });
  
  // Оптимизация: если нет клиентов, сразу возвращаем пустой массив
  if (!clients.length) return [];
  
  // Оптимизация: если нет фильтров и поиска, возвращаем исходный массив
  if (!isFilteringActive(filters, searchQuery)) {
    console.log('Фильтры не активны, возвращаем весь список');
    return [...clients];
  }
  
  // Очищаем поисковый запрос от форматирования для сравнения с телефоном
  const cleanedSearchQuery = searchQuery.replace(/[-\s()+]/g, '');
  
  const filteredClients = clients.filter(client => {
    // ИСПРАВЛЕНО: Строгий поиск только по ID и телефону
    if (searchQuery) {
      // Очищаем телефон от символов для удобства поиска
      const cleanedPhone = client.phone ? client.phone.replace(/[-\s()+]/g, '') : '';
      
      // Проверяем точное совпадение по ID
      const matchesId = client.id === searchQuery;
      
      // Проверяем совпадение по телефону
      const matchesPhone = cleanedPhone.includes(cleanedSearchQuery);
      
      // Если не соответствует ни ID, ни телефону - исключаем из результатов
      if (!(matchesId || matchesPhone)) {
        return false;
      }
    }
    
    // Фильтр по статусу клиента
    if (filters.status && filters.status !== 'all' && client.status !== filters.status) {
      return false;
    }
    
    // ВАЖНО: Проверка на точное совпадение количества заказов
    // Если значения в полях ordersFrom и ordersTo одинаковые и не пустые, 
    // проверяем на точное совпадение
    if (filters.ordersFrom && filters.ordersTo && 
        filters.ordersFrom === filters.ordersTo && 
        filters.ordersFrom.trim() !== '') {
      
      const exactOrderCount = parseInt(filters.ordersFrom, 10);
      if (!isNaN(exactOrderCount) && client.ordersCount !== exactOrderCount) {
        return false;
      }
    } else {
      // Стандартная проверка на диапазон
      
      // Фильтр по количеству заказов (минимум)
      if (filters.ordersFrom && filters.ordersFrom.trim() !== '') {
        const ordersFrom = parseFloat(filters.ordersFrom);
        if (!isNaN(ordersFrom) && client.ordersCount < ordersFrom) {
          return false;
        }
      }
      
      // Фильтр по количеству заказов (максимум)
      if (filters.ordersTo && filters.ordersTo.trim() !== '') {
        const ordersTo = parseFloat(filters.ordersTo);
        if (!isNaN(ordersTo) && client.ordersCount > ordersTo) {
          return false;
        }
      }
    }
    
    // Аналогичная логика для суммы заказов
    if (filters.amountFrom && filters.amountTo && 
        filters.amountFrom === filters.amountTo && 
        filters.amountFrom.trim() !== '') {
      
      const exactAmount = parseFloat(filters.amountFrom);
      if (!isNaN(exactAmount) && client.totalAmount !== exactAmount) {
        return false;
      }
    } else {
      // Фильтр по сумме заказов (минимум)
      if (filters.amountFrom && filters.amountFrom.trim() !== '') {
        const amountFrom = parseFloat(filters.amountFrom);
        if (!isNaN(amountFrom) && client.totalAmount < amountFrom) {
          return false;
        }
      }
      
      // Фильтр по сумме заказов (максимум)
      if (filters.amountTo && filters.amountTo.trim() !== '') {
        const amountTo = parseFloat(filters.amountTo);
        if (!isNaN(amountTo) && client.totalAmount > amountTo) {
          return false;
        }
      }
    }
    
    // Фильтр по дате последнего заказа (от)
    if (filters.lastOrderFrom && client.lastOrder) {
      const lastOrderDate = new Date(client.lastOrder);
      const fromDate = new Date(filters.lastOrderFrom);
      
      if (lastOrderDate < fromDate) {
        return false;
      }
    }
    
    // Фильтр по дате последнего заказа (до)
    if (filters.lastOrderTo && client.lastOrder) {
      const lastOrderDate = new Date(client.lastOrder);
      const toDate = new Date(filters.lastOrderTo);
      toDate.setHours(23, 59, 59, 999); // Устанавливаем конец дня
      
      if (lastOrderDate > toDate) {
        return false;
      }
    }
    
    // Фильтр по источнику привлечения
    if (filters.leadsource && client.leadsource !== filters.leadsource) {
      return false;
    }
    
    // Клиент прошел все проверки фильтров
    return true;
  });
  
  console.log(`После фильтрации осталось ${filteredClients.length} клиентов`);
  return filteredClients;
};

/**
 * Сортирует список клиентов по указанному полю и направлению
 * 
 * @param clients Исходный список клиентов
 * @param column Поле для сортировки
 * @param direction Направление сортировки ('asc' или 'desc')
 * @returns Отсортированный список клиентов
 */
export const sortClients = (
  clients: Client[],
  column: string,
  direction: 'asc' | 'desc'
): Client[] => {
  // Оптимизация: если нет клиентов, сразу возвращаем пустой массив
  if (!clients.length) return [];
  
  return [...clients].sort((a, b) => {
    let valueA: any = (a as any)[column];
    let valueB: any = (b as any)[column];
    
    // Специальная обработка для разных типов данных
    
    // Для строк - переводим в нижний регистр для корректного сравнения
    if (typeof valueA === 'string') {
      valueA = valueA.toLowerCase();
    }
    if (typeof valueB === 'string') {
      valueB = valueB.toLowerCase();
    }
    
    // Для числовых значений - преобразуем в числа
    if (column === 'totalAmount' || column === 'ordersCount') {
      valueA = Number(valueA) || 0;
      valueB = Number(valueB) || 0;
    }
    
    // Для дат - преобразуем в объекты Date
    if (column === 'lastOrder' || column === 'created') {
      valueA = valueA ? new Date(valueA).getTime() : 0;
      valueB = valueB ? new Date(valueB).getTime() : 0;
    }
    
    // Сравнение значений с учетом направления сортировки
    if (valueA < valueB) {
      return direction === 'asc' ? -1 : 1;
    }
    if (valueA > valueB) {
      return direction === 'asc' ? 1 : -1;
    }
    return 0;
  });
};

/**
 * Применяет пагинацию к отфильтрованному списку клиентов
 * 
 * @param clients Отфильтрованный список клиентов
 * @param currentPage Текущая страница
 * @param pageSize Размер страницы
 * @returns Список клиентов для текущей страницы
 */
export const paginateClients = (
  clients: Client[],
  currentPage: number,
  pageSize: number
): Client[] => {
  // Оптимизация: если нет клиентов, сразу возвращаем пустой массив
  if (!clients.length) return [];
  
  // Оптимизация: если размер страницы 0 или отрицательный, возвращаем пустой массив
  if (pageSize <= 0) return [];
  
  // Корректируем текущую страницу, если она некорректна
  const safeCurrentPage = Math.max(1, currentPage);
  const startIndex = (safeCurrentPage - 1) * pageSize;
  
  // Оптимизация: если стартовый индекс больше длины массива, возвращаем пустой массив
  if (startIndex >= clients.length) return [];
  
  const endIndex = Math.min(startIndex + pageSize, clients.length);
  return clients.slice(startIndex, endIndex);
};

/**
 * Определяет, активны ли фильтры или поиск
 * 
 * @param filters Объект с параметрами фильтрации
 * @param searchQuery Строка поиска
 * @returns True, если какой-либо фильтр активен
 */
export const isFilteringActive = (
  filters: ClientFilters,
  searchQuery: string
): boolean => {
  // Поиск активен, если строка поиска не пуста
  const hasSearchQuery = searchQuery && searchQuery.trim() !== '';
  
  // Проверяем наличие хотя бы одного активного фильтра
  const hasActiveFilters = Object.entries(filters).some(([key, value]) => {
    if (key === 'status') return value !== 'all';
    
    // Для числовых полей проверяем, что это не пустая строка и не строка с пробелами
    if (typeof value === 'string') {
      return value.trim() !== '';
    }
    
    return Boolean(value);
  });
  
  const result = hasSearchQuery || hasActiveFilters;
  console.log(`Фильтрация активна: ${result} (поиск: ${hasSearchQuery}, фильтры: ${hasActiveFilters})`);
  return result;
};